import { FormField } from "@/components/forms/FormField.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import { showWarn } from "@/modules/error/errorStream.ts";
import { LocationSelectCustom } from "@/modules/location/LocationSelect.tsx";
import { MultiLocationSelectCustom } from "@/modules/location/MultiLocationSelect.tsx";
import type { LocationsForType } from "@/modules/location/location.ts";
import { useLingui } from "@lingui/react/macro";
import { Controller, useFormContext } from "react-hook-form";

/** NB: form must implement this */
export interface LocationInputs {
    location: string;
}

export interface LocationFieldProps {
    locations: LocationsForType;
    useNewSelect?: boolean;
    afterChange?: (location: string) => void;
}

export function LocationField({ locations, useNewSelect, afterChange }: LocationFieldProps) {
    const form = useFormContext<LocationInputs>();
    const { control } = form;
    return (
        <Controller
            control={control}
            name="location"
            render={({ field }) => {
                return (
                    <LocationFieldCustom
                        value={field.value}
                        onChange={(v) => {
                            field.onChange(v);
                            afterChange?.(v);
                        }}
                        locations={locations}
                        useNewSelect={useNewSelect}
                    />
                );
            }}
        />
    );
}

export function LocationFieldCustom({
    value,
    onChange,
    locations: { locations, showLocations },
    useNewSelect,
}: LocationFieldProps & {
    value: string;
    onChange: (location: string) => void;
}) {
    const { t } = useLingui();
    if (!showLocations) {
        showWarn("LocationField: This component should not be rendered when showLocation is false.");
        return null;
    }

    return (
        <FormField
            data-testid="location-field"
            wide
            label={t`Location`}
            isRequired
            description={t`Data center where the resource will be created.`}
        >
            {useNewSelect ? (
                <LocationSelectCustom items={locations} valueKey={value} onChange={(l) => onChange(l.slug)} />
            ) : (
                <RadioGroupDiv>
                    <MultiLocationSelectCustom locations={locations} defaultValue={value} onChange={onChange} />
                </RadioGroupDiv>
            )}
        </FormField>
    );
}
